import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useApp } from '../context/AppContext'
import { 
  Download, 
  ArrowLeft, 
  Heart, 
  Activity, 
  Shield, 
  Eye, 
  Brain, 
  Zap,
  Loader2,
  AlertCircle
} from 'lucide-react'

function ReportPage() {
  const { scanId } = useParams()
  const navigate = useNavigate()
  const { getReport, downloadPDF, isLoading } = useApp()
  const [report, setReport] = useState(null)
  const [error, setError] = useState(null)

  // Safe formatter for nested values
  const formatValue = (value) => {
    if (value === null || value === undefined) return 'N/A'

    // Numbers: round to 2 decimals
    if (typeof value === 'number') return Math.round((value + Number.EPSILON) * 100) / 100

    if (typeof value !== 'object') return value

    // Common shapes
    if (Object.prototype.hasOwnProperty.call(value, 'value')) {
      const v = value.value
      return typeof v === 'number' ? Math.round((v + Number.EPSILON) * 100) / 100 : v
    }
    if (Object.prototype.hasOwnProperty.call(value, 'min') && Object.prototype.hasOwnProperty.call(value, 'max')) {
      const min = typeof value.min === 'number' ? Math.round((value.min + Number.EPSILON) * 100) / 100 : value.min
      const max = typeof value.max === 'number' ? Math.round((value.max + Number.EPSILON) * 100) / 100 : value.max
      const unit = value.unit ? ` ${value.unit}` : ''
      return `${min}-${max}${unit}`
    }

    // Compact pretty string for generic objects (first 3 entries)
    try {
      const entries = Object.entries(value)
      const parts = entries.slice(0, 3).map(([k, v]) => {
        if (v && typeof v === 'object') {
          if ('value' in v) return `${k.replace(/_/g, ' ')}: ${formatValue(v.value)}`
          const inner = Object.entries(v)
            .slice(0, 2)
            .map(([ik, iv]) => `${ik.replace(/_/g, ' ')}: ${formatValue(iv)}`)
            .join(', ')
          return `${k.replace(/_/g, ' ')}: { ${inner}${inner && inner.at(-1) !== '...' && inner.length === 2 && entries.length > 3 ? ', ...' : ''} }`
        }
        return `${k.replace(/_/g, ' ')}: ${formatValue(v)}`
      })
      return parts.join(', ')
    } catch {
      return JSON.stringify(value)
    }
  }

  // Render a flat object as key/value rows
  const renderObjectRows = (obj) => {
    if (!obj || typeof obj !== 'object') return null
    return (
      <div className="space-y-2">
        {Object.entries(obj).map(([k, v]) => (
          <div key={k} className="flex justify-between text-sm">
            <span className="text-gray-600 capitalize">{k.replace(/_/g, ' ')}</span>
            <span className="font-medium text-gray-900">{formatValue(v)}</span>
          </div>
        ))}
      </div>
    )
  }

  // Render possibly nested objects at one extra level (e.g., left/right)
  const renderNestedObject = (title, obj) => {
    if (!obj || typeof obj !== 'object') return null
    const entries = Object.entries(obj)
    const hasNested = entries.some(([, v]) => v && typeof v === 'object' && !('value' in v))

    if (!hasNested) {
      return (
        <div className="mt-4">
          <h4 className="text-sm font-semibold text-gray-800 mb-2">{title}</h4>
          {renderObjectRows(obj)}
        </div>
      )
    }

    return (
      <div className="mt-4">
        <h4 className="text-sm font-semibold text-gray-800 mb-3">{title}</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {entries.map(([k, v]) => (
            <div key={k} className="p-3 bg-gray-50 rounded-lg">
              <div className="text-xs text-gray-500 mb-1 capitalize">{k.replace(/_/g, ' ')}</div>
              {typeof v === 'object' ? renderObjectRows(v) : (
                <div className="text-sm font-medium text-gray-900">{formatValue(v)}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    )
  }

  useEffect(() => {
    if (scanId) {
      loadReport()
    }
  }, [scanId])

  const loadReport = async () => {
    try {
      const reportData = await getReport(scanId)
      setReport(reportData)
    } catch (err) {
      setError(err.message || 'Failed to load report')
    }
  }

  const handleDownloadPDF = async () => {
    try {
      await downloadPDF(scanId)
    } catch (err) {
      console.error('Failed to download PDF:', err)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary-blue" />
          <p className="text-gray-600">Loading report...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="card max-w-md mx-auto text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => navigate('/')}
            className="btn-primary"
          >
            Go Home
          </button>
        </div>
      </div>
    )
  }

  if (!report) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">No report data available</p>
        </div>
      </div>
    )
  }

  const healthCategories = [
    {
      icon: Heart,
      title: 'Cardiovascular Health',
      color: 'text-red-500',
      data: report.cardiovascular || {}
    },
    {
      icon: Activity,
      title: 'Body Composition',
      color: 'text-blue-500',
      data: report.body_composition || {}
    },
    {
      icon: Shield,
      title: 'Skin Health',
      color: 'text-green-500',
      data: report.skin_health || {}
    },
    {
      icon: Eye,
      title: 'Eye Health',
      color: 'text-purple-500',
      data: report.eye_health || {}
    },
    {
      icon: Brain,
      title: 'Mental Wellness',
      color: 'text-yellow-500',
      data: report.mental_wellness || {}
    },
    {
      icon: Zap,
      title: 'Nutritional Indicators',
      color: 'text-orange-500',
      data: report.nutritional_indicators || {}
    }
  ]

  return (
    <div className="min-h-screen bg-gray-50 py-4">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/history')}
            className="flex items-center text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to History
          </button>
          
          <button
            onClick={handleDownloadPDF}
            className="btn-primary flex items-center"
          >
            <Download className="w-5 h-5 mr-2" />
            Download PDF Report
          </button>
        </div>

        {/* Report Header */}
        <div className="card mb-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Health Analysis Report
            </h1>
            <p className="text-gray-600 mb-6">
              Comprehensive health analysis completed on {new Date(report.timestamp).toLocaleDateString()}
            </p>
            
            {/* Captured Photo Display */}
            {report.captured_image_url && (
              <div className="mb-8 flex justify-center">
                <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-4 border border-blue-200 shadow-lg max-w-md">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Captured Face Scan</h3>
                  <div className="relative rounded-xl overflow-hidden shadow-md border-2 border-blue-300">
                    <img
                      src={(() => {
                        // Construct full URL for the image
                        const apiBase = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1'
                        const baseUrl = apiBase.replace('/api/v1', '')
                        return `${baseUrl}${report.captured_image_url}`
                      })()}
                      alt="Face scan capture"
                      className="w-full h-auto object-cover"
                      onError={(e) => {
                        // Hide image and show error message
                        const container = e.target.parentElement
                        if (container) {
                          e.target.style.display = 'none'
                          const errorDiv = container.querySelector('.image-error')
                          if (errorDiv) {
                            errorDiv.style.display = 'flex'
                          }
                        }
                      }}
                    />
                    <div className="image-error hidden items-center justify-center bg-gray-100 p-8 text-gray-500 absolute inset-0">
                      <div className="text-center">
                        <p className="text-sm">Image not available</p>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2 italic">Photo captured during face scan</p>
                </div>
              </div>
            )}
            
            {/* Three Column Layout: Patient Info | Health Score | Analysis Quality */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              
              {/* Left: Patient Information */}
              {report.user_details && (
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-4 border border-blue-200 h-full flex flex-col">
                  <div className="flex items-center mb-3">
                    <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center mr-2 shadow-sm">
                      <Shield className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-base font-semibold text-blue-900">Patient Information</h3>
                  </div>
                  
                  <div className="space-y-2 flex-1">
                    <div className="grid grid-cols-2 gap-2">
                      <div className="text-center p-2 bg-white rounded-lg shadow-sm border border-blue-100">
                        <div className="text-xs text-blue-600 mb-1 font-medium">Name</div>
                        <div className="font-semibold text-blue-900 text-xs">{report.user_details.name}</div>
                      </div>
                      <div className="text-center p-2 bg-white rounded-lg shadow-sm border border-blue-100">
                        <div className="text-xs text-blue-600 mb-1 font-medium">Age</div>
                        <div className="font-semibold text-blue-900 text-xs">{report.user_details.age} years</div>
                      </div>
                      <div className="text-center p-2 bg-white rounded-lg shadow-sm border border-blue-100">
                        <div className="text-xs text-blue-600 mb-1 font-medium">Gender</div>
                        <div className="font-semibold text-blue-900 text-xs capitalize">{report.user_details.gender}</div>
                      </div>
                      <div className="text-center p-2 bg-white rounded-lg shadow-sm border border-blue-100">
                        <div className="text-xs text-blue-600 mb-1 font-medium">Height</div>
                        <div className="font-semibold text-blue-900 text-xs">{report.user_details.height_cm} cm</div>
                      </div>
                      <div className="text-center p-2 bg-white rounded-lg shadow-sm border border-blue-100">
                        <div className="text-xs text-blue-600 mb-1 font-medium">Weight</div>
                        <div className="font-semibold text-blue-900 text-xs">{report.user_details.weight_kg} kg</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Center: Overall Health Score */}
              <div className="flex flex-col">
                <div className="relative overflow-hidden bg-gradient-to-br from-primary-600 via-primary-700 to-secondary-600 rounded-2xl p-4 text-white shadow-2xl h-full flex flex-col justify-center">
                  {/* Background Pattern */}
                  <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-white/20 to-transparent"></div>
                    <div className="absolute -top-2 -right-2 w-12 h-12 bg-white/10 rounded-full"></div>
                    <div className="absolute -bottom-2 -left-2 w-16 h-16 bg-white/5 rounded-full"></div>
                  </div>
                  
                  {/* Content */}
                  <div className="relative z-10 text-center">
                    <div className="flex items-center justify-center mb-2">
                      <div className="w-2 h-2 bg-white/30 rounded-full mr-1"></div>
                      <h2 className="text-sm font-semibold tracking-wide">Overall Health Score</h2>
                      <div className="w-2 h-2 bg-white/30 rounded-full ml-1"></div>
                    </div>
                    
                    <div className="relative mb-2">
                      <div className="text-4xl font-bold tracking-tight drop-shadow-lg">
                        {report.overall_scores?.health_score || 'N/A'}
                      </div>
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-white/20 rounded-full flex items-center justify-center">
                        <div className="w-1 h-1 bg-white rounded-full"></div>
                      </div>
                    </div>
                    
                    <div className="text-sm font-medium opacity-90 tracking-wide mb-2">out of 100</div>
                    
                    {/* Score Indicator */}
                    <div className="flex justify-center">
                      <div className="flex space-x-1">
                        {[1, 2, 3, 4, 5].map((i) => (
                          <div
                            key={i}
                            className={`w-1.5 h-1.5 rounded-full ${
                              (report.overall_scores?.health_score || 0) >= i * 20
                                ? 'bg-white'
                                : 'bg-white/30'
                            }`}
                          ></div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: Analysis Quality */}
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-4 border border-green-200 h-full flex flex-col">
                <div className="flex items-center mb-3">
                  <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center mr-2 shadow-sm">
                    <Activity className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-base font-semibold text-green-900">Analysis Quality</h3>
                </div>
                
                <div className="space-y-2 flex-1">
                  <div className={`rounded-lg p-3 text-center border ${
                    report.data_quality?.quality_warning ? 
                      'bg-yellow-50 border-yellow-200' : 
                      'bg-green-50 border-green-200'
                  }`}>
                    <div className="space-y-2">
                      <div>
                        <h4 className={`text-xs font-semibold ${
                          report.data_quality?.quality_warning ? 'text-yellow-700' : 'text-green-700'
                        }`}>
                          Data Quality
                        </h4>
                        <p className={`text-xs mt-1 ${
                          report.data_quality?.quality_warning ? 'text-yellow-600' : 'text-green-600'
                        }`}>
                          {report.data_quality?.recommendation || 
                            (report.analysis_method === 'real_rppg' ? 
                              '✅ AI-Powered Face Analysis with rPPG' : 
                              '⚠️ Rule-Based Analysis (Limited Data)')}
                        </p>
                      </div>
                      
                      <div className="flex justify-center">
                        <div className={`text-xs px-2 py-1 rounded-full ${
                          report.data_quality?.quality_warning ? 
                            'bg-yellow-100 text-yellow-800' : 
                            'bg-green-100 text-green-800'
                        }`}>
                          {report.data_quality?.has_real_data ? 'HIGH QUALITY' : 'LIMITED DATA'}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {report.data_quality && (
                    <div className="space-y-2">
                      <div className="p-2 bg-white rounded-lg shadow-sm border border-green-100">
                        <div className="text-xs text-green-600 mb-1 font-medium">MediaPipe Landmarks</div>
                        <div className="text-xs font-semibold text-green-900">{report.data_quality.landmark_count || 0} frames</div>
                      </div>
                      <div className="p-2 bg-white rounded-lg shadow-sm border border-green-100">
                        <div className="text-xs text-green-600 mb-1 font-medium">Confidence Score</div>
                        <div className="text-xs font-semibold text-green-900">{Math.round((report.data_quality.confidence_score || 0.5) * 100)}%</div>
                      </div>
                      {report.data_quality?.quality_warning && (
                        <div className="p-2 bg-yellow-50 rounded-lg border border-yellow-200">
                          <div className="text-xs text-yellow-700 font-medium">
                            ⚠️ Results based on limited data - consult healthcare professional
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Health Categories */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {healthCategories.map((category, index) => (
            <div key={index} className="group relative overflow-hidden bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-gray-200">
              {/* Card Header */}
              <div className="relative p-6 pb-4">
                <div className="flex items-center mb-4">
                  <div className={`p-3 rounded-xl bg-gradient-to-br ${category.color === 'text-red-500' ? 'from-red-50 to-red-100' : 
                    category.color === 'text-blue-500' ? 'from-blue-50 to-blue-100' :
                    category.color === 'text-green-500' ? 'from-green-50 to-green-100' :
                    category.color === 'text-purple-500' ? 'from-purple-50 to-purple-100' :
                    category.color === 'text-yellow-500' ? 'from-yellow-50 to-yellow-100' :
                    'from-orange-50 to-orange-100'} shadow-sm`}>
                    <category.icon className={`w-6 h-6 ${category.color}`} />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 ml-3">
                    {category.title}
                  </h3>
                </div>
                
                {/* Metrics */}
                <div className="space-y-3">
                  {Object.entries(category.data).slice(0, 3).map(([key, value]) => (
                    <div key={key} className="flex justify-between items-center py-2 px-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <span className="text-sm font-medium text-gray-600 capitalize">
                        {key.replace(/_/g, ' ')}
                      </span>
                      <span className="text-sm font-bold text-gray-900">
                        {formatValue(value)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Card Footer */}
              <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-gray-100 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Health Category</span>
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                    <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                    <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Detailed Analysis */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 mb-8 overflow-hidden">
          <div className="bg-gradient-to-r from-primary-50 to-secondary-50 px-8 py-6 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              <div className="w-8 h-8 bg-gradient-to-br from-primary-600 to-secondary-600 rounded-lg flex items-center justify-center mr-3">
                <Activity className="w-5 h-5 text-white" />
              </div>
              Detailed Analysis
            </h2>
            <p className="text-gray-600 mt-2">Comprehensive health metrics and insights</p>
          </div>
          
          <div className="p-4 md:p-8">
            <div className="space-y-8">
              {/* Cardiovascular */}
              {report.cardiovascular && (
                <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-2xl p-6 border border-red-200">
                  <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                    <div className="w-10 h-10 bg-red-500 rounded-xl flex items-center justify-center mr-3 shadow-sm">
                      <Heart className="w-6 h-6 text-white" />
                    </div>
                    Cardiovascular Health
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-white rounded-xl shadow-sm border border-red-100 hover:shadow-md transition-shadow">
                      <div className="text-3xl font-bold text-red-600 mb-1">
                        {report.cardiovascular.heart_rate?.value || 'N/A'}
                      </div>
                      <div className="text-sm text-gray-600 font-medium">Heart Rate (BPM)</div>
                    </div>
                    <div className="text-center p-4 bg-white rounded-xl shadow-sm border border-red-100 hover:shadow-md transition-shadow">
                      <div className="text-3xl font-bold text-red-600 mb-1">
                        {report.cardiovascular.respiratory_rate?.value || 'N/A'}
                      </div>
                      <div className="text-sm text-gray-600 font-medium">Respiratory Rate</div>
                    </div>
                    <div className="text-center p-4 bg-white rounded-xl shadow-sm border border-red-100 hover:shadow-md transition-shadow">
                      <div className="text-3xl font-bold text-red-600 mb-1">
                        {formatValue(report.cardiovascular.hrv?.value)}
                      </div>
                      <div className="text-sm text-gray-600 font-medium">HRV (ms)</div>
                    </div>
                    <div className="text-center p-4 bg-white rounded-xl shadow-sm border border-red-100 hover:shadow-md transition-shadow">
                      <div className="text-3xl font-bold text-red-600 mb-1">
                        {report.cardiovascular.spo2?.value || 'N/A'}
                      </div>
                      <div className="text-sm text-gray-600 font-medium">SpO2 (%)</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Body Composition */}
              {report.body_composition && (
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 border border-blue-200">
                  <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                    <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center mr-3 shadow-sm">
                      <Activity className="w-6 h-6 text-white" />
                    </div>
                    Body Composition
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="text-center p-4 bg-white rounded-xl shadow-sm border border-blue-100 hover:shadow-md transition-shadow">
                      <div className="text-3xl font-bold text-blue-600 mb-1">
                        {formatValue(report.body_composition.bmi?.value)}
                      </div>
                      <div className="text-sm text-gray-600 font-medium">BMI</div>
                    </div>
                    <div className="text-center p-4 bg-white rounded-xl shadow-sm border border-blue-100 hover:shadow-md transition-shadow">
                      <div className="text-3xl font-bold text-blue-600 mb-1">
                        {formatValue(report.body_composition.body_fat?.value)}
                      </div>
                      <div className="text-sm text-gray-600 font-medium">Body Fat (%)</div>
                    </div>
                    <div className="text-center p-4 bg-white rounded-xl shadow-sm border border-blue-100 hover:shadow-md transition-shadow">
                      <div className="text-3xl font-bold text-blue-600 mb-1">
                        {formatValue(report.body_composition.metabolic_age?.value)}
                      </div>
                      <div className="text-sm text-gray-600 font-medium">Metabolic Age</div>
                    </div>
                    <div className="text-center p-4 bg-white rounded-xl shadow-sm border border-blue-100 hover:shadow-md transition-shadow">
                      <div className="text-3xl font-bold text-blue-600 mb-1">
                        {formatValue(report.body_composition.visceral_fat?.level)}
                      </div>
                      <div className="text-sm text-gray-600 font-medium">Visceral Fat</div>
                    </div>
                  </div>
                  {/* Additional Metrics */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-white rounded-xl shadow-sm border border-blue-100">
                      <div className="text-sm text-gray-500 mb-2 font-medium">Ideal Weight</div>
                      <div className="text-lg font-bold text-gray-900">{formatValue(report.body_composition.ideal_weight)}</div>
                    </div>
                    <div className="p-4 bg-white rounded-xl shadow-sm border border-blue-100">
                      <div className="text-sm text-gray-500 mb-2 font-medium">Lean Mass</div>
                      <div className="text-lg font-bold text-gray-900">{formatValue(report.body_composition.lean_mass_estimate)}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Recommendations */}
              {report.recommendations && report.recommendations.length > 0 && (
                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-6 border border-green-200">
                  <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                    <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center mr-3 shadow-sm">
                      <Shield className="w-6 h-6 text-white" />
                    </div>
                    Health Recommendations
                  </h3>
                  <div className="space-y-4">
                    {report.recommendations.map((rec, index) => (
                      <div key={index} className="flex items-start p-5 bg-white rounded-xl shadow-sm border border-green-100 hover:shadow-md transition-shadow">
                        <div className={`w-8 h-8 rounded-xl flex items-center justify-center text-sm font-bold mr-4 mt-0.5 shadow-sm ${
                          rec.priority === 'high' ? 'bg-red-500 text-white' :
                          rec.priority === 'medium' ? 'bg-yellow-500 text-white' :
                          'bg-green-500 text-white'
                        }`}>
                          {rec.priority === 'high' ? 'H' : rec.priority === 'medium' ? 'M' : 'L'}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900 mb-2 text-lg">
                            {rec.category.charAt(0).toUpperCase() + rec.category.slice(1)}
                          </h4>
                          <p className="text-gray-700 leading-relaxed">{rec.recommendation_text}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Health Insights */}
              {report.health_insights && report.health_insights.length > 0 && (
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-6 border border-purple-200">
                  <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                    <div className="w-10 h-10 bg-purple-500 rounded-xl flex items-center justify-center mr-3 shadow-sm">
                      <Brain className="w-6 h-6 text-white" />
                    </div>
                    Health Insights
                  </h3>
                  <div className="space-y-3">
                    {report.health_insights.map((insight, index) => (
                      <div key={index} className="flex items-start p-4 bg-white rounded-xl shadow-sm border border-purple-100 hover:shadow-md transition-shadow">
                        <div className="w-3 h-3 bg-purple-500 rounded-full mt-2 mr-4 flex-shrink-0" />
                        <p className="text-gray-700 leading-relaxed">{insight}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Disclaimers */}
              {report.disclaimers && (
                <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-2xl p-6 border border-red-200">
                  <h3 className="text-xl font-semibold text-red-900 mb-4 flex items-center">
                    <div className="w-10 h-10 bg-red-500 rounded-xl flex items-center justify-center mr-3 shadow-sm">
                      <AlertCircle className="w-6 h-6 text-white" />
                    </div>
                    Important Medical Disclaimer
                  </h3>
                  <div className="space-y-3">
                    {report.disclaimers.map((disclaimer, index) => (
                      <div key={index} className="flex items-start p-4 bg-white rounded-xl shadow-sm border border-red-100">
                        <div className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-4 flex-shrink-0" />
                        <p className="text-sm text-red-800 leading-relaxed">{disclaimer}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ReportPage

